import React, { useState, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2 } from 'lucide-react';

// Set up PDF.js worker source
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const UpdatesThumbnail = ({ pdfUrl }) => {
  const [containerWidth, setContainerWidth] = useState(280);
  const [containerHeight, setContainerHeight] = useState(330);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const calculateDimensions = useCallback(() => {
    let width, height;
    if (window.innerWidth <= 480) {
      width = window.innerWidth * 0.9;
      height = width * 1.3;
    } else if (window.innerWidth <= 768) {
      width = window.innerWidth * 0.7;
      height = width * 1.4;
    } else if (window.innerWidth <= 1024) {
      width = 350;
      height = width * 1.5;
    } else {
      width = 400;
      height = width * 1.45;
    }
    return { width, height };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const { width, height } = calculateDimensions();
      setContainerWidth(width);
      setContainerHeight(height);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateDimensions]);

  useEffect(() => {
    const zoomFactor = 1.1;
    setPageWidth(containerWidth * zoomFactor);
    setPageHeight(containerHeight * zoomFactor);
  }, [containerWidth, containerHeight]);

  const onDocumentLoadSuccess = () => {
    setIsLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error) => {
    setError('Error loading PDF. Please try again later.');
    setIsLoading(false);
  };

  const LoadingComponent = () => (
    <div className="flex flex-col items-center justify-center p-4 space-y-2">
      <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      <p className="text-sm text-gray-500">Loading PDF...</p>
    </div>
  );

  const ErrorComponent = ({ message }) => (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
      <p className="text-sm">{message}</p>
    </div>
  );

  return (
    <div className="pdf-div text-gray-700 flex flex-col items-center justify-center p-2 sm:p-4">
      <div
        className="pdf-container border border-gray-200 rounded-lg overflow-hidden shadow-lg bg-white"
        style={{
          width: containerWidth,
          height: containerHeight,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {error ? (
          <ErrorComponent message={error} />
        ) : (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={<LoadingComponent />}
            error={<ErrorComponent message="Failed to load PDF" />}
          >
            {!error && !isLoading && (
              <Page
                pageNumber={1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={pageWidth}
                height={pageHeight}
                scale={1}
                className="shadow-sm"
              />
            )}
          </Document>
        )}
      </div>
    </div>
  );
};

export default UpdatesThumbnail;